import { GetAllFunction, GetFunction, Model, ModelId } from "@/components/inputs/components/combo-box/model-combo-box";
import { BaseQuery } from "@/shared/types/base";
import { Filter, FilterData } from "./filter";
import { ReactNode } from "react";

export interface ModelFilterProps<T extends Model = any, P extends BaseQuery = any> {
    type: "model";
    name: string;
    label: string;
    initialValue?: ModelId;
    get: GetFunction<T>,
    getAll: GetAllFunction<T, P>,
    labelName?: keyof T,
    renderOption?: (option: T) => ReactNode,
}

export interface ModelFilterReturn<T extends Model = any, P extends BaseQuery = any> {
    type: "model";
    key: string;
    name: string;
    label: string;
    value: ModelId;
    initialValue: ModelId;
    get: GetFunction<T>,
    getAll: GetAllFunction<T, P>,
    labelName: keyof T,
    renderOption: (option: T) => ReactNode,
    active: boolean;
    getCriteria:()=>Promise<string>;
}

export class ModelFilter<T extends Model, P extends BaseQuery> extends Filter {
    name: string;
    label: string;
    initialValue: string;
    get: GetFunction<T>;
    getAll: GetAllFunction<T, P>;
    labelName: keyof T;
    renderOption: (option: T) => ReactNode;

    constructor({ name, label, initialValue = "", get, getAll, labelName = "nombre", renderOption = (o) => o[labelName] }: ModelFilterProps) {
        super();

        this.name = name;
        this.label = label;
        this.initialValue = initialValue;
        this.get = get;
        this.getAll = getAll;
        this.labelName = labelName as any;
        this.renderOption = renderOption;
    }

    public getInitialValues(result: FilterData): void {
        result[this.name] = this.initialValue;
    }

    public getFilter(filterData: FilterData): ModelFilterReturn {
        const value = this.getValue(filterData);
        return {
            type: "model",
            key: this.name,
            name: this.name,
            label: this.label,
            initialValue: this.initialValue,
            get: this.get,
            getAll: this.getAll,
            labelName: this.labelName,
            renderOption: this.renderOption,
            value: value,
            active: value !== this.initialValue,
            getCriteria: ()=>this.getCriteria(filterData)
        }
    }

    public parse(filterData: FilterData, result: FilterData): void {
        const value = this.getValue(filterData);
        if (value !== this.initialValue) result[this.name] = value;
    }

    public async getCriteria(filterData: FilterData): Promise<string> {
        const value = this.getValue(filterData);
        const active = value !== this.initialValue;
        if(!active) return "";
        const model = await this.get(value);
        return `${this.label} = ${model ? model[this.labelName] : "???"}`
    }

    private getValue(filterData: FilterData): string {
        const value = filterData[this.name];
        return value == null ? this.initialValue : String(value);
    }
}